import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CategoryData } from "@/hooks/use-dashboard-analytics";
import { useValuesVisibility } from "@/hooks/use-values-visibility";

interface CategoryExpenseChartProps {
  data: CategoryData[];
  isLoading?: boolean;
  compact?: boolean;
}

export function CategoryExpenseChart({ data, isLoading, compact }: CategoryExpenseChartProps) {
  const { mask } = useValuesVisibility();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Gastos por Categoria</CardTitle>
          <CardDescription>Distribuição do mês atual</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.slice(0, 6); // Top 6 categories
  const totalAmount = chartData.reduce((sum, item) => sum + item.amount, 0);

  // Generate chart config dynamically from data
  const chartConfig = Object.fromEntries(
    chartData.map(item => [
      item.id,
      { label: item.name, color: item.color }
    ])
  );

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Gastos por Categoria</CardTitle>
          <CardDescription>Distribuição do mês atual</CardDescription>
        </CardHeader>
        <CardContent className="h-[280px] flex items-center justify-center">
          <p className="text-muted-foreground text-sm">Nenhuma despesa este mês</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Gastos por Categoria</CardTitle>
        <CardDescription>Distribuição do mês atual</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className={compact ? "h-[180px] w-full" : "h-[280px] w-full"}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={compact ? 40 : 60}
                outerRadius={compact ? 65 : 90}
                paddingAngle={2}
                dataKey="amount"
                nameKey="name"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                ))}
              </Pie>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, name) => (
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">
                          {mask(new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(Number(value)))}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {((Number(value) / totalAmount) * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  />
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
        
        {/* Custom Legend */}
        {!compact && <div className="flex flex-wrap gap-3 justify-center mt-2">
          {chartData.slice(0, 4).map((item) => (
            <div key={item.id} className="flex items-center gap-1.5">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs text-muted-foreground">{item.name}</span>
            </div>
          ))}
        </div>}
      </CardContent>
    </Card>
  );
}
